import { Icon, IconButton, merge, MessageBar, MessageBarType, Spinner, Stack, StackItem, TextField } from '@fluentui/react';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import paperClipIcon from '../../../assets/icons/svg/paperClip.svg';
import planeIcon from '../../../assets/icons/svg/plane.svg';
import { ResponsiveSize, useResponsiveSize } from '../../../contexts/responsiveContext';
import { useTchat, useTchatManager } from '../../../contexts/tchatContext';
import { byteArrayVersBase64 } from '../../../helpers';
import SvgIcon from '../../SvgIcon';

const Inputs = () => {

    const { isOnline, message, sendingInProgress } = useTchat();
    const { resetAttachment, resetParent, sendMessage, updateMessage } = useTchatManager();
    const { responsiveSize } = useResponsiveSize();
    const { fileData, fileName, idMessageParent, texte, texteMessageParent } = message;

    const fileUploadRef = useRef();

    const [error, setError] = useState(undefined);

    const styles = useMemo(
        () => {
            let styles = {
                fileName: { root: { marginBottom: - 4 } },
                infoContainer: {
                    root: {
                        alignItems: "center",
                        backgroundColor: "#f4f7f9",
                        color: "#565867",
                        padding: "3px 0px 3px 12px",
                    }
                },
                icon: { icon: { fontSize: "10px" } },
                inner: {
                    root: {
                        backgroundColor: "#EEEEEE",
                        padding: 12,
                    }
                },
                messageBar: {
                    content: { alignItems: "center" },
                    text: { marginBottom: 4 },
                },
                textField: {
                    field: {
                        color: "#777777",
                        padding: "unset",
                        resize: "none",
                        selectors: {
                            "::placeholder": { color: "#777777" },
                            "&:disabled": { background: "#EEEEEE" }
                        },
                    },
                    fieldGroup: {
                        backgroundColor: "#EEEEEE",
                        height: "100%",
                    },
                    root: {
                        flexGrow: 1,
                        height: 75
                    },
                    wrapper: { height: "100%" }
                },
                spinner: {
                    circle: {
                        height: 17,
                        width: 17
                    },
                    root: {
                        padding: "0 4px",
                        width: 32
                    }
                }
            };

            if (responsiveSize >= ResponsiveSize.medium)
                styles = merge(
                    styles,
                    {
                        inner: { root: { padding: 16 } },
                        outer: {
                            root: {
                                //marginTop: 24
                                background: "white",
                                marginLeft: 1,
                                marginRight: 1,
                                paddingTop: 24,
                            }
                        },
                        textField: { root: { height: 110 } }
                    }
                );

            return styles;
        },
        [responsiveSize]
    );

    const onFileUpload = useCallback((event) => {
        const MAX_FILE_SIZE = 3;
        const file = event.target.files[0];
        const fsize = Math.ceil((file.size / 1024 / 1024));
        if (fsize <= MAX_FILE_SIZE) {
            const reader = new FileReader();
            reader.addEventListener("load", () => {
                updateMessage({
                    fileData: byteArrayVersBase64(reader.result),
                    fileName: file.name,
                });
            })
            reader.readAsArrayBuffer(file);
        } else {
            resetAttachment();
            setError(`Document trop volumineux (${MAX_FILE_SIZE} Mo max.)`);
        }
    }, [resetAttachment, updateMessage]);

    const onKeyUp = useCallback((event) => {
        if (event.key === "Enter" && event.ctrlKey)
            sendMessage();
    }, [sendMessage]);

    return <Stack styles={styles.outer}>

        {!!error &&
            <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError(undefined)} styles={styles.messageBar}>
                {error}
            </MessageBar>
        }

        {!!fileData && !!fileName &&
            <Stack horizontal styles={styles.infoContainer} tokens={{ childrenGap: 8 }}>
                <Icon iconName="OpenFile" styles={styles.icon} />
                <StackItem grow styles={styles.fileName}>
                    Pièce jointe : {fileName}
                </StackItem>
                <IconButton
                    disabled={sendingInProgress}
                    iconProps={{ iconName: "ChromeClose" }}
                    onClick={resetAttachment}
                    styles={styles.icon}
                    title="Retirer la pièce jointe"
                />
            </Stack>
        }

        {!!idMessageParent &&
            <Stack horizontal styles={styles.infoContainer} tokens={{ childrenGap: 8 }}>
                <Icon iconName="Reply" styles={styles.icon} />
                <Stack.Item grow>
                    Vous répondez à ce message : {texteMessageParent}
                </Stack.Item>
                <IconButton
                    disabled={sendingInProgress}
                    iconProps={{ iconName: "ChromeClose" }}
                    onClick={resetParent}
                    styles={styles.icon}
                    title="Annuler la réponse à un message"
                />
            </Stack>

        }

        <Stack horizontal styles={styles.inner} verticalAlign="start">
            <TextField
                autoComplete="off"
                autoFocus
                borderless
                disabled={!isOnline || sendingInProgress}
                maxLength={2000}
                multiline
                onChange={(event, newValue) => updateMessage({ texte: newValue })}
                onKeyUp={onKeyUp}
                placeholder={isOnline ? "Votre message..." : "Impossible de se connecter au serveur de messagerie..."}
                styles={styles.textField}
                value={texte}
            />
            <input type="file" ref={fileUploadRef} onChange={onFileUpload} style={{ display: "none" }} />
            <SvgIcon
                disabled={sendingInProgress}
                height={17}
                onClick={() => !!fileUploadRef.current && fileUploadRef.current.click()}
                src={paperClipIcon}
                title="Joindre un fichier"
            />
            {sendingInProgress ?
                <Spinner styles={styles.spinner} /> :
                <SvgIcon
                    disabled={(!texte && !fileName) || sendingInProgress}
                    height={17}
                    onClick={sendMessage}
                    src={planeIcon}
                    title="Envoyer"
                />
            }
        </Stack>

    </Stack>;

};

export default Inputs;